// The real available-time chips for the selected treatment chain. Fed by
// deriveFeasibleStarts (staff=any slots, filtered by therapist + time-of-day,
// keeping only starts for which the whole chain resolves). A chip's label is
// ChiDesk's own start_time — tapping one sets the booking anchor to that slot's
// EXACT fake-Z start.
//
// States: loading → shimmer; ready+chips → chips; ready+empty → "no
// availability, try another day"; error-anchor → "couldn't load live times" +
// Retry + a manual-time fallback (books an UNVERIFIED time, by design — the
// create-time validation + venue alert are the backstop).

import type { FeasibleResult } from "../../state/chidesk-availability";

export function TimeChips({
  feasible,
  pickedStart,
  onPick,
  dateLabel,
  onRetry,
  manualStart,
  onManualStart,
  onNextDay,
}: {
  feasible: FeasibleResult;
  pickedStart: string | null;
  onPick: (start: string) => void;
  dateLabel: string;
  onRetry: () => void;
  manualStart: string | null;
  onManualStart: (v: string | null) => void;
  onNextDay: () => void;
}) {
  if (feasible.status === "loading") {
    return (
      <div className="vt-chidesk-times" aria-busy="true">
        <span className="vt-chidesk-therapists-label">Available times · {dateLabel}</span>
        <div className="vt-time-chips">
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <span key={i} className="vt-chidesk-time-chip vt-chidesk-time-chip-shimmer" aria-hidden="true" />
          ))}
        </div>
      </div>
    );
  }

  if (feasible.status === "error-anchor") {
    return (
      <div className="vt-chidesk-times">
        <span className="vt-chidesk-therapists-label">Available times · {dateLabel}</span>
        <div className="vt-chidesk-times-note">
          We couldn&rsquo;t load live times right now.
          <button type="button" className="vt-chidesk-linkbtn" onClick={onRetry}>
            Retry
          </button>
        </div>
        <label className="vt-chidesk-manual">
          <span>Or enter a time</span>
          <input
            type="time"
            value={manualStart ?? ""}
            onChange={(e) => onManualStart(e.target.value ? e.target.value : null)}
          />
        </label>
      </div>
    );
  }

  // status === 'ready'
  if (feasible.chips.length === 0) {
    return (
      <div className="vt-chidesk-times">
        <span className="vt-chidesk-therapists-label">Available times · {dateLabel}</span>
        <div className="vt-chidesk-times-empty">
          No availability on {dateLabel}.
          <button type="button" className="vt-chidesk-linkbtn" onClick={onNextDay}>
            Try the next day →
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="vt-chidesk-times">
      <span className="vt-chidesk-therapists-label">Available times · {dateLabel}</span>
      <div className="vt-time-chips">
        {feasible.chips.map((c) => {
          const active = c.start === pickedStart;
          return (
            <button
              key={c.start}
              type="button"
              className={`vt-chidesk-time-chip${active ? " vt-chidesk-time-chip-active" : ""}`}
              onClick={() => onPick(c.start)}
              aria-pressed={active}
            >
              {c.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
